import { useCallback, useState } from 'react';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useReceptionStrings } from '../lib/receptionStrings';
import { receptionRowOps } from '../lib/receptionRowOps';
import { userErrorMessage } from '../lib/userErrorMessage';
import { errorCode } from './useDoctorBooking';

// ─────────────────────────────────────────────────────────────────────────────
// پذیرش's row ops (PM-0518) — ONE hook for both surfaces: which ops a row offers
// (lib/receptionRowOps.js), what each does, and the confirm/note overlays'
// state. The desktop menu and the phone sheet only render what this returns;
// `ReceptionRowOpsDialogs` renders the overlays.
//
// Feedback (DESIGN_CONCEPT §2.4): a row that changes on screen IS the feedback —
// «عدم‌حضور» repaints its chip, «نفر بعدی» moves it to the head of the queue, the
// note appears under the name. A cancelled row LEAVES the list, so that one is
// said in words. A failure is a toast (§7: a row action has no form to report
// into), in the doctor's language: the codes the BFF refuses with map to this
// screen's own strings, and anything else is the BFF's keyed sentence or a
// generic line — never transport text.
// ─────────────────────────────────────────────────────────────────────────────

const REFUSAL_KEYS = {
  APPOINTMENT_HAS_PAYMENT: 'dashboard.rowOps.errors.hasPayment',
  CANCEL_NEEDS_GRANT: 'dashboard.rowOps.errors.needsGrant',
  PAYMENT_CHECK_UNAVAILABLE: 'dashboard.rowOps.errors.paymentCheck',
  SUBSCRIPTION_REQUIRED: 'dashboard.rowOps.errors.readOnly',
  NOT_FOUND: 'dashboard.rowOps.errors.gone',
};

const LABEL_KEYS = {
  next: 'dashboard.rowOps.next',
  backToRoom: 'dashboard.rowOps.backToRoom',
  edit: 'dashboard.edit.action',
  note: 'dashboard.rowOps.note',
  nextAppt: 'dashboard.rowOps.nextAppt',
  noShow: 'dashboard.rowOps.noShow',
  cancel: 'dashboard.rowOps.cancel',
};

/** What a failed row op says — exported for tests. */
export function rowOpFailure(err, { i18n, s }, fallbackKey) {
  const key = REFUSAL_KEYS[errorCode(err)];
  if (key) return s(key);
  if (err?.extensions?.messageKey) return userErrorMessage(err, i18n);
  return s(fallbackKey);
}

/**
 * @param {object} args
 * @param {object}   args.reception  useReception's return (the writes)
 * @param {object}   args.booking    useReceptionBooking's return (clinics, openBook)
 * @param {Function} args.arrive     the screen's «ثبت حضور» (pending set + toast)
 * @param {object}   [args.toast]
 * @param {boolean}  args.isToday
 * @param {boolean}  args.isPast
 */
export function useReceptionRowOps({ reception, booking, arrive, toast, isToday, isPast }) {
  const i18n = useTranslation();
  const s = useReceptionStrings();
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [pendingCancel, setPendingCancel] = useState(null);
  const [pendingNoShow, setPendingNoShow] = useState(null);
  const [noteFor, setNoteFor] = useState(null);

  const nameOf = useCallback(
    (apt) => apt?.name || apt?.patient?.name || i18n.t('common.labels.patient'),
    [i18n],
  );

  // One write at a time per row; the row's ops stand down while it runs.
  const runFor = useCallback(async (apt, write, fallbackKey) => {
    if (!apt?.id) return false;
    setBusyIds((prev) => new Set(prev).add(apt.id));
    try {
      await write();
      return true;
    } catch (err) {
      toast?.error?.(rowOpFailure(err, { i18n, s }, fallbackKey));
      return false;
    } finally {
      setBusyIds((prev) => { const n = new Set(prev); n.delete(apt.id); return n; });
    }
  }, [toast, i18n, s]);

  const select = useCallback((id, apt) => {
    switch (id) {
      case 'arrive':
      case 'backToRoom':
        arrive?.(apt.id);
        break;
      case 'next':
        runFor(apt, () => reception.setNextInQueue(apt.id), 'dashboard.rowOps.errors.next');
        break;
      case 'edit':
        booking.askEdit?.(apt);
        break;
      case 'note':
        setNoteFor(apt);
        break;
      case 'nextAppt':
        booking.openBook?.({
          // Its numbers ride along, so the form shows «+N» as the row did (PM-0536).
          patient: { id: apt.patientId, name: nameOf(apt), phone: apt.patient?.phone || '', phones: apt.patient?.phones || [] },
          clinicId: apt.clinicId || null,
        });
        break;
      case 'noShow':
        setPendingNoShow(apt);
        break;
      case 'cancel':
        setPendingCancel(apt);
        break;
      default:
    }
  }, [arrive, runFor, reception, booking, nameOf]);

  const opsFor = useCallback((apt, queuePos = null) => {
    const busy = busyIds.has(apt?.id);
    return receptionRowOps({
      apt, isToday, isPast, queuePos,
      clinics: booking?.bookingClinics?.clinics || [],
      canBook: Boolean(booking?.canBook),
    }).map((op) => ({
      ...op,
      label: op.id === 'arrive' ? i18n.t('dashboard.queue.arrived') : s(LABEL_KEYS[op.id]),
      disabled: busy,
      onSelect: () => select(op.id, apt),
    }));
  }, [busyIds, isToday, isPast, booking, i18n, s, select]);

  const confirmCancel = useCallback(async () => {
    const apt = pendingCancel;
    setPendingCancel(null);
    const ok = await runFor(apt, () => reception.cancelAppointment(apt.id), 'dashboard.rowOps.errors.cancel');
    if (ok) toast?.success?.(s('dashboard.rowOps.cancelled', { name: nameOf(apt) }));
  }, [pendingCancel, runFor, reception, toast, s, nameOf]);

  const confirmNoShow = useCallback(async () => {
    const apt = pendingNoShow;
    setPendingNoShow(null);
    await runFor(apt, () => reception.markNoShow(apt.id), 'dashboard.rowOps.errors.noShow');
  }, [pendingNoShow, runFor, reception]);

  // The note overlay is a form: it reports its own failure inline, so this one
  // rejects instead of toasting, with the sentence to show.
  const saveNote = useCallback(async (note) => {
    const apt = noteFor;
    if (!apt?.id) return;
    try {
      await reception.setAppointmentNote(apt.id, note);
      setNoteFor(null);
    } catch (err) {
      throw new Error(rowOpFailure(err, { i18n, s }, 'dashboard.rowOps.errors.note'));
    }
  }, [noteFor, reception, i18n, s]);

  return {
    s,
    nameOf,
    opsFor,
    pendingCancel,
    cancelCancel: useCallback(() => setPendingCancel(null), []),
    confirmCancel,
    pendingNoShow,
    cancelNoShow: useCallback(() => setPendingNoShow(null), []),
    confirmNoShow,
    noteFor,
    closeNote: useCallback(() => setNoteFor(null), []),
    saveNote,
  };
}

export default useReceptionRowOps;
